import axios from 'axios'

// JWT token 記憶體快取
let accessToken = null
let refreshPromise = null // 防止多次同時刷新

// 設定 axios 預設配置
axios.defaults.withCredentials = true // 支援 httpOnly cookies

// 取得 CSRF token
function getCsrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

// 設定 access token
export function setAccessToken(token) {
  accessToken = token
}

// 清除 access token
export function clearAccessToken() {
  accessToken = null
}

// 取得 access token
export function getAccessToken() {
  return accessToken
}

// 刷新 access token
async function refreshAccessToken() {
  // 防止多次同時刷新
  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = (async () => {
    try {
      const response = await axios.post('/api/v1/auth/refresh', {}, {
        headers: {
          'X-CSRF-Token': getCsrfToken(),
          'Accept': 'application/json'
        },
        withCredentials: true
      })

      if (response.data.success) {
        const newToken = response.data.access_token
        setAccessToken(newToken)
        return newToken
      } else {
        throw new Error('Token 刷新失敗')
      }
    } catch (error) {
      console.warn('Token 刷新失敗:', error.message)
      clearAccessToken()
      
      // 重導向到登入頁面或清除認證狀態
      const { useUserStore } = await import('../stores/user.js')
      const userStore = useUserStore()
      userStore.clearUserData()
      
      throw error
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// 請求攔截器 - 自動添加 Authorization header
axios.interceptors.request.use(
  (config) => {
    // 添加 CSRF token
    if (!config.headers['X-CSRF-Token']) {
      config.headers['X-CSRF-Token'] = getCsrfToken()
    }

    // 添加 Access Token（如果有的話）
    if (accessToken && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${accessToken}`
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 回應攔截器 - 處理 token 過期
axios.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // 檢查是否為 401 錯誤且未嘗試過刷新
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        // 嘗試刷新 token
        const newToken = await refreshAccessToken()
        
        // 使用新 token 重試原請求
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`
        return axios(originalRequest)
      } catch (refreshError) {
        // 刷新失敗，重導向到登入頁面
        console.warn('自動刷新 token 失敗:', refreshError.message)
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  }
)

// 背景定時刷新機制
let refreshTimer = null

export function startTokenRefreshTimer() {
  // 清除現有的定時器
  if (refreshTimer) {
    clearInterval(refreshTimer)
  }

  // 每 10 分鐘檢查一次是否需要刷新
  refreshTimer = setInterval(async () => {
    if (accessToken) {
      try {
        await refreshAccessToken()
        console.log('背景定時刷新 token 成功')
      } catch (error) {
        console.warn('背景定時刷新 token 失敗:', error.message)
      }
    }
  }, 10 * 60 * 1000) // 10 分鐘
}

export function stopTokenRefreshTimer() {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = null
  }
}

// 監聽頁面可見性變化，重新檢查認證狀態
document.addEventListener('visibilitychange', async () => {
  if (!document.hidden && accessToken) {
    try {
      // 頁面重新可見時，檢查並刷新 token
      await refreshAccessToken()
      console.log('頁面重新可見，token 刷新成功')
    } catch (error) {
      console.warn('頁面重新可見，token 刷新失敗:', error.message)
    }
  }
})

// 監聽 online/offline 事件
window.addEventListener('online', async () => {
  if (accessToken) {
    try {
      await refreshAccessToken()
      console.log('網路重新連接，token 刷新成功')
    } catch (error) {
      console.warn('網路重新連接，token 刷新失敗:', error.message)
    }
  }
})

export default axios